// @ts-strict-ignore
import { Color, TagsInputBlock } from '@coast/schemas';
import { isEmpty, isNil, uniqBy } from 'lodash';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { MBButtonCappedWidth } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { defaultFieldPresenter } from '@shared/components/blocks/lib/field';
import { getTagOption } from '@shared/components/blocks/lib/tags';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { TagList } from '@shared/components/blocks/lib/tags/tagList';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelectModal } from '@shared/components/multiSelect';
import { ColorThemeContext, grid } from '@shared/styles';
import { TagOption, useWorkflowTagOptions } from '@shared/util/hooks/useWorkflowTagOptions';
import { i18n } from '@shared/util/i18n';
import { CardRenderContext } from '../../contexts/render';
import { MBCardInputBlockBase } from '../input/base';
import { renderClearButton, renderLabel as renderDefaultLabel } from '../input/utils';
import { MBCreateTagRow } from '../tags/createTagRow';
import { useArchiveCardTag, useCreateCardTag, useUpdateCardTag } from './hooks';

interface Props {
  block: TagsInputBlock;
}

export function MBCardInputBlockTags({ block }: Props) {
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const { setFieldValue, submitForm, values } = useCardFormContext();
  const [recentlyCreatedTagOptions, setRecentlyCreatedTagOptions] = useState<TagOption[]>([]);

  const { options, loading, refetch } = useWorkflowTagOptions(block.name);
  const optionsToRender = useMemo(
    () => uniqBy([...recentlyCreatedTagOptions, ...options], 'value'),
    [options, recentlyCreatedTagOptions],
  );

  const labelExtractor = useCallback(value => getTagOption(value, options)?.label, [options]);
  const idExtractor = useCallback(value => getTagOption(value, options)?.id, [options]);
  const tagValues = useMemo<string[]>(() => values[block.name] || [], [values, block.name]);
  const optionSlugs = useMemo(() => optionsToRender.map(opt => opt.value), [optionsToRender]);

  const archivedValues = useMemo<string[]>(
    () => optionSlugs.filter(value => !!getTagOption(value, options)?.archived),
    [optionSlugs, options],
  );

  const onChange = useCallback(
    async (selectedTags: string[]): Promise<void> => {
      setFieldValue(block.name, isEmpty(selectedTags) ? null : selectedTags);
      if (renderContext !== 'form') {
        await submitForm();
      }
    },
    [block.name, setFieldValue, renderContext, submitForm],
  );
  const renderRight = useMemo(() => {
    if (!tagValues.length || block.required || block.validation?.min > 0) {
      return;
    }
    return renderClearButton;
  }, [tagValues, block]);

  const createCardTag = useCreateCardTag(block.name);
  const updateCardTag = useUpdateCardTag();
  const archiveCardTag = useArchiveCardTag();
  const createAndSelectCardTag = useCallback(
    async (name: string) => {
      const tag = await createCardTag(name);
      const newTagOption: TagOption = {
        id: tag.id,
        value: tag.slug,
        label: tag.label,
        backgroundColor: tag.colorName,
        archived: tag.archivedAt != null,
        fieldName: tag.fieldName,
        sortKey: tag.sortKey,
      };
      const newTagSlugs = [...tagValues, tag.slug];
      if (!!block.validation?.max) {
        onChange(newTagSlugs.slice(newTagSlugs.length - block.validation.max));
      } else {
        onChange(newTagSlugs);
      }

      setRecentlyCreatedTagOptions(prevValue => [newTagOption, ...prevValue]);
    },
    [createCardTag, onChange, tagValues, block.validation?.max],
  );

  const onChangeLabel = useCallback(
    async (value: string, label: string) => {
      const id = idExtractor(value);
      await updateCardTag(id, label);
    },
    [idExtractor, updateCardTag],
  );

  const onArchive = useCallback(
    async (value: string) => {
      const id = idExtractor(value);
      await archiveCardTag(id, false);
    },
    [archiveCardTag, idExtractor],
  );

  const onUnarchive = useCallback(
    async (value: string) => {
      const id = idExtractor(value);
      await archiveCardTag(id, true);
    },
    [archiveCardTag, idExtractor],
  );

  const renderLabel = useMemo(
    () => (!!tagValues.length && block.appearance !== 'button' ? renderDefaultLabel : null),
    [tagValues, block.appearance],
  );

  const content = useMemo<ReactNode>(() => {
    if (loading) {
      return <MBLoadingIndicator />;
    }

    if (block.appearance === 'button') {
      return <MBButtonCappedWidth size="small" theme="outline" title={block.label} />;
    }

    if (tagValues?.length) {
      return (
        <View row wrap shrinkWrap paddingVertical={0.5}>
          <TagList fieldName={block.name} tagValues={tagValues} />
        </View>
      );
    }

    return defaultFieldPresenter(
      block.placeholder,
      'text',
      block.textStyle,
      colors.foregroundInactive,
    );
  }, [block, tagValues, colors, loading]);

  const renderItem = useCallback(
    (value: string) => {
      const option = getTagOption(value, options);
      return (
        <View row centerVertically flex>
          <Tag color={option.backgroundColor as Color} label={option.label} />
        </View>
      );
    },
    [options],
  );

  const renderArchivedItem = useCallback(
    (value: string) => {
      const option = getTagOption(value, options);
      return (
        <View row paddingLeft={4} centerVertically flex>
          <Tag archived label={option.label} />
        </View>
      );
    },
    [options],
  );

  const renderCreateNew = useCallback(
    (query: string, creating: boolean) => {
      if (creating) {
        return (
          <View style={{ height: grid(8) }} center>
            <MBLoadingIndicator />
          </View>
        );
      }

      return <MBCreateTagRow query={query} numberOfOptions={options.length} />;
    },
    [options.length],
  );

  const match = useCallback(
    (value: string, query: string, exact: boolean) => {
      const label = labelExtractor(value);
      if (isNil(label)) {
        return false;
      }
      if (exact) {
        return label.toLowerCase() === query.toLowerCase();
      }
      return label.toLowerCase().includes(query.toLowerCase());
    },
    [labelExtractor],
  );

  const isButton = block.appearance === 'button';
  const anchorName = isButton ? `--input-tags-button-${block.name}` : `--input-tags-${block.name}`;

  return (
    <MBMultiSelectModal<string>
      label={block.label}
      anchorName={anchorName}
      testID={`tag-sheet-${block.name}`}
      onOpen={refetch}
      onChange={onChange}
      onCreate={block.readOnly === false && createAndSelectCardTag}
      onChangeLabel={onChangeLabel}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      selectAll
      selectAllLabel={i18n('card.blocks.tagsInput.selectAllLabel')}
      max={block.validation?.max}
      isRequired={block.required}
      renderItem={renderItem}
      renderArchivedItem={renderArchivedItem}
      renderCreateNew={renderCreateNew}
      values={optionSlugs}
      selectedValues={tagValues}
      labelExtractor={labelExtractor}
      match={match}
      archivedValues={archivedValues}
      itemLabel={i18n('card.blocks.tagsInput.itemLabel')}
      itemLabelPlural={i18n('card.blocks.tagsInput.itemLabelPlural')}
      errorLabel={i18n('errors.tag.enterName')}
      itemsEditable={block.readOnly === false}
    >
      <MBCardInputBlockBase block={block} renderRight={renderRight} renderLabel={renderLabel}>
        <View testID={`input-block-${block.name}`} style={{ anchorName }}>
          {content}
        </View>
      </MBCardInputBlockBase>
    </MBMultiSelectModal>
  );
}
